"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/contexts/auth-context";

export function AuthGuard({ children }: { children: React.ReactNode }) {
	const { currentUser, loading } = useAuth();
	const router = useRouter();
	const [isClient, setIsClient] = useState(false);

	// Set isClient to true once component mounts
	useEffect(() => {
		setIsClient(true);
	}, []);

	useEffect(() => {
		// Only run after initial loading is complete and we're on client
		if (!loading && isClient) {
			// If no Firebase user, redirect to login
			if (!currentUser) {
				console.log("No authenticated user, redirecting to login");
				router.push("/login");
			}
		}
	}, [currentUser, loading, router, isClient]);

	// Show loading indicator while checking auth state
	if (loading || !isClient) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
			</div>
		);
	}

	// Only render children if user is authenticated
	return currentUser ? <>{children}</> : null;
}

export function GuestGuard({ children }: { children: React.ReactNode }) {
	const { currentUser, loading } = useAuth();
	const router = useRouter();
	const [isClient, setIsClient] = useState(false);

	// Set isClient to true once component mounts
	useEffect(() => {
		setIsClient(true);
	}, []);

	useEffect(() => {
		if (!loading && isClient) {
			// If Firebase user exists, redirect to dashboard
			if (currentUser) {
				router.push("/dashboard");
			}
		}
	}, [currentUser, loading, router, isClient]);

	// Show loading indicator while checking auth state
	if (loading || !isClient) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
			</div>
		);
	}

	// Only render children if user is not authenticated
	return !currentUser ? <>{children}</> : null;
}
